import { memo, useState } from 'react';
import styled from '@emotion/styled';

import { useCurrentRoute } from '../../../../lib/useCurrentRoute';
import useTracking from '../../../../lib/useTracking';
import zIndices from '../../../../lib/zIndices';
import ScreenReaderOnly from '../../../ScreenReaderOnly';
import MobileNavList from './MobileNavList';

import type { ThemeProps } from '@emotion/react';
import type { NavProps as DesktopNavProps } from '../Desktop/Nav';
import type { CSSObject } from '@emotion/styled';
import HamburgerIcon from '../../../Icons/HamburgerIcon';

interface MobileNavWrapProps {
  open: boolean;
}

const MobileNavWrap = styled('nav')`
  left: 0;
  overflow: hidden;
  position: fixed;
  top: 0;
  transition: all 0.5s;
  width: 100%;
  z-index: ${zIndices.mobileNav};
  background-color: ${(props: ThemeProps): string =>
    props.theme.colors.background};

  ${(props: MobileNavWrapProps): string =>
    props.open ? 'height: 100%;' : ' height: 0;'}
`;

const MobileNavListWrap = styled('div')`
  padding-top: 56px;
  height: 100vh;
  overflow-y: auto;
`;

const NavButton = styled('button')`
  background: none;
  border: none;
  font-size: 10px;
  height: 40px;
  min-width: 40px;
  border-radius: 3px;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: ${zIndices.mobileNavButton};

  ${(props: ThemeProps): CSSObject | undefined =>
    props.theme.components.Nav?.Button}
`;

const LiteMobileButtonText = styled('span')`
  display: inline-block;
  position: absolute;
  top: 12px;
  left: 60px;
  font-size: 18px;
  text-transform: uppercase;
  z-index: ${zIndices.mobileNavButton};
  ${({ theme }: ThemeProps): CSSObject | undefined =>
    theme.liteSite?.Nav?.Button}
`;

const userDecisionArea = 'mobile nav menu';

interface NavProps extends DesktopNavProps {
  showMenuText?: boolean | undefined;
}

function MobileNavAndButton({ pages, showMenuText }: NavProps) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const currentRoute = useCurrentRoute();

  const trackGuest = useTracking('guest', currentRoute, userDecisionArea);

  const trackNavMenuToggle = useTracking(
    'leftNav',
    currentRoute,
    `nav menu toggle`
  );

  const onClick = (): void => {
    const selection = mobileNavOpen ? 'close' : 'open';
    trackNavMenuToggle(`${selection} menu`);

    trackGuest(`${selection} menu`);

    setMobileNavOpen((prevOpen) => {
      return !prevOpen;
    });
  };

  return (
    <>
      <NavButton onClick={onClick} data-testid="MobileNav__Button">
        <HamburgerIcon isOpen={mobileNavOpen} />

        {showMenuText ? (
          <LiteMobileButtonText>Menu</LiteMobileButtonText>
        ) : (
          <ScreenReaderOnly>Menu Button</ScreenReaderOnly>
        )}
      </NavButton>

      <MobileNavWrap open={mobileNavOpen} role="navigation">
        <MobileNavListWrap>
          <MobileNavList
            pages={pages}
            setMobileNavOpen={setMobileNavOpen}
            userDecisionArea={userDecisionArea}
          />
        </MobileNavListWrap>
      </MobileNavWrap>
    </>
  );
}

export default memo(MobileNavAndButton);
